/**
 * VE-29142 — hold the TDO lock on documents the reviewer has unsaved work in,
 * not just the one on screen.
 *
 * THE RULE, deliberately narrow: a lock is held for the document being edited
 * (already handled — see the heartbeat in `pages/DocumentDetails`) plus any
 * document visited in this session that still has unsaved changes. Nothing
 * else. A file merely looked at and left clean holds nothing, because taking a
 * lock for it would block colleagues from work the reviewer is not doing.
 *
 * WHY IT IS NEEDED. `suspendedSessions` holds exactly the dirty-and-switched-
 * away-from documents, and `DocumentDetails` releases its lock on unmount —
 * which a switch triggers. So before this, a reviewer could leave unsaved
 * redactions in a file and a colleague could open and edit it freely, with the
 * reviewer's Save All later writing straight over their work.
 *
 * WHY A SAGA. The component can only know about the document it is rendering.
 * The lock assessment already names this as the right shape —
 * `plans/document-editor-lock-assessment.md` §2 weakness 3 ("lifecycle in the
 * component … belongs in a saga") and §4 ("saga-owned lifecycle").
 *
 * WHAT THIS DOES NOT DO. It does not touch the active document's lock, which
 * keeps its existing component-driven heartbeat for parity with the video
 * editor. Two lifecycles is not the end state — §4 wants one — but collapsing
 * them means rewriting the video path too, and that is not this ticket.
 *
 * HOW IT TAKES THE LOCK, and why not the way everything else does. Background
 * documents go through `serviceRefreshBackgroundTdoLock`, NOT `checkTdoLock`.
 * The latter's handlers write the single global `tdoLockInfo` without reading
 * the tdoId off the response, which is safe only while the caller is the
 * document on screen — fired for a parked one it overwrites the on-screen
 * document's verdict, and can clear a genuine lock on the file being edited.
 * The service writes our own record and asks nothing about ownership. See its
 * comment in `../services`.
 *
 * The lock remains ADVISORY and race-prone on acquire (§2 weaknesses 1 and 5);
 * this widens WHICH documents are covered, and changes nothing about how good
 * the covering is.
 *
 * VE-31538 adds one more id to the same set: the request's PROJECT TDO, when
 * the open document is a member of its merged document. That one is held for
 * the document ON SCREEN as well as for parked ones — opening a member locks
 * the set — which is the one place this saga reaches past suspended sessions.
 * ASKING IS SEPARATE FROM TAKING. The verdict is read on every pass, and
 * only the WRITE answers to `selectMayHoldTdoLock` — because a session can
 * be read-only for reasons that say nothing about whether it may hold a
 * lock, and because the reviewer staring at a file a colleague holds is
 * exactly the one who needs telling that the rest of the set is theirs too.
 * A pass that finds no set to report publishes `null` rather than staying
 * silent, or the last verdict outlives the document it was about.
 *
 * `mayTake` comes from `selectMayHoldTdoLock` rather than from
 * `rolePermissions.lockTdo`, and that distinction is load-bearing twice
 * over: `lockTdo` is cleared by the very verdict this loop publishes, and it
 * also gates the PER-FILE lock heartbeat, so neither reading it nor
 * widening it is safe.
 *
 * It rides here because the lifecycle it needs is already in this loop:
 * refresh on every tick, release when no longer wanted, release on close or
 * logout. The WRITE itself is not the background service but
 * `serviceRefreshProjectSetLock`, which reads the set's record before
 * writing — members share one record, so a session that wrote first and
 * looked second would see its own neighbours and lock everyone out of a set
 * nobody was editing. That service also publishes who holds the set, which
 * is how the editor goes read-only without `checkTdoLock` overwriting the
 * open file's own verdict.
 */
import {
  actionChannel,
  delay,
  put,
  race,
  select,
  take,
} from 'typed-redux-saga/macro';
import { buffers } from 'redux-saga';

import { LOCK_REFRESH } from '@helpers/constants';
// The service thunk, not the REMOVE_TDO_LOCK action: that action is consumed
// by `watchRemoveTdoLock`, which is `takeLatest`. Fanning N releases through a
// single-flight watcher works today only because its body is one
// non-suspending `put` — an `await` added there would cancel every release but
// the last, and leak locks silently.
import { removeTdoLock as removeTdoLockService } from '@common-modules/mediaDetails/services';
import { selectUser } from '@helpers/tdoHelper';
import {
  selectFeatureFlags,
  selectMayHoldTdoLock,
} from '@common/user-permissions';
import { modules } from '@veritone/glc-redux';
import { LOAD_PROJECT_SUCCESS } from '@redact-modules/projects/actions';
import {
  selectEditorProjectTdoId,
  selectLockableProjectTdoId,
} from '@redact-modules/projects/lockableProject';
import { CLOSE_DOCUMENT, OPEN_DOCUMENT_SUCCESS } from '../actions';
import { isSessionWorthKeeping } from '../models';
import { selectSession, selectSuspendedSessions } from '../selectors';
import {
  serviceRefreshBackgroundTdoLock,
  serviceRefreshProjectSetLock,
} from '../services';
import { setMergedSetBlock } from '@common-modules/mediaDetails/actions/view';

const {
  user: { LOGOUT },
} = modules;

export function* suspendedLocksSaga() {
  /**
   * A BUFFERED CHANNEL, not a bare `take`.
   *
   * `take` only catches what is dispatched while the saga is parked on it,
   * and these two arrive within milliseconds of each other on a cold open —
   * so whichever landed while the previous pass was still running was simply
   * lost, and the set went unreported until the next 60-second tick. That is
   * the "no banner on the first document, banner on the second" shape.
   *
   * Sliding(1): the wakes are not events to process, they are a signal that
   * the world moved. Two of them mean the same single pass.
   */
  const wake = yield* actionChannel(
    [OPEN_DOCUMENT_SUCCESS, LOAD_PROJECT_SUCCESS],
    buffers.sliding(1)
  );

  // Ids this saga believes it holds, so it can tell an acquire from a refresh
  // and release exactly what it took. Local rather than in the store: it is
  // this loop's bookkeeping, and nothing else should act on it.
  let held: string[] = [];

  /**
   * Release several locks.
   *
   * One `put` per id would go through `watchRemoveTdoLock`, which is
   * `takeLatest` — single-flight by construction. It happens to work today
   * only because that watcher's body is a single non-suspending `put`; the day
   * someone adds an `await` to it, every release but the last is cancelled and
   * locks leak with nothing to show for it. Depending on an unstated
   * scheduling property of a watcher this module does not own is not worth the
   * one-line saving, so this drives the thunk itself.
   */
  const release = function* (ids: string[], userId: string) {
    for (const tdoId of ids) {
      yield* put(removeTdoLockService({ tdoId, userId }));
    }
  };

  while (true) {
    const { flags, user } = {
      flags: yield* select(selectFeatureFlags),
      user: yield* select(selectUser),
    };
    const userId = user?.userId;

    if (flags?.tdoLock && userId) {
      // Filtered, not assumed. The reducer only PARKS a dirty document, but
      // Save All persists suspended documents in place, so a parked session
      // can go clean while it is off screen. Holding its lock after that keeps
      // colleagues out of a file nobody is editing, for the rest of the
      // editor session — the release below then picks it up on the same tick.
      const suspended = yield* select(selectSuspendedSessions);
      // `isSessionWorthKeeping`, not `hasUnsavedWork`: the latter excludes
      // `saving`, so a heartbeat landing mid-Save-All released the lock on the
      // document being written. If that save then failed, nothing re-acquired
      // it until the next tick — up to a full LOCK_REFRESH unlocked with real
      // unsaved work in it.
      const wanted = Object.values(suspended)
        .filter((session) => isSessionWorthKeeping(session.save))
        .map((session) => String(session.tdoId));

      // VE-31538: plus the project TDO, when the document on screen is a
      // member of the request's merged document. Unlike every other id here
      // this one is NOT about unsaved work — opening a member locks the set,
      // because the app does not sync concurrent edits across members and a
      // rebuild reads all of them.
      //
      // ASKING IS NOT TAKING, and conflating the two was a bug worth naming.
      // `lockTdo` is false for a view-only reviewer, who must not lock a
      // whole set by looking at one file in it — but it is ALSO false
      // whenever the open file is already locked by somebody else, because
      // that resolves to the same view-only permission set. Gating the whole
      // errand on it meant the one case where the reviewer most needs to be
      // told the set is in use was the case where we never asked.
      //
      // So the project is resolved either way, and `mayTake` carries the
      // permission down to the WRITE alone. URL review mode does NOT take
      // the set (R5): such a tab is review for its whole life and can change
      // nothing about the set, so holding it would shut seven operations for
      // everyone else. It still takes the FILE lock, which is what review
      // mode has always been for.
      const active = yield* select(selectSession);
      // NOT `rolePermissions.lockTdo`. A foreign set lock clears that flag,
      // and this loop is what publishes the foreign set lock — so reading it
      // here let the verdict switch off the service that produces it. The
      // flag also gates the PER-FILE lock heartbeat, so widening it instead
      // let a read-only bystander hold the open member and then take the set
      // from its real holder. `selectMayHoldTdoLock` asks the same question
      // minus the set-lock band, which is the one this loop owns.
      const mayTake = yield* select(selectMayHoldTdoLock);
      const projectTdoId = yield* select(
        selectLockableProjectTdoId(active?.tdoId)
      );
      // Whether the request has a merged document AT ALL, which is a
      // different question from whether the open document is in it — and the
      // two want opposite verdicts when `projectTdoId` comes back undefined.
      const requestProjectTdoId = yield* select(selectEditorProjectTdoId);

      // Anything we hold that is no longer wanted has been saved, restored as
      // the active document, or discarded.
      //
      // The ACTIVE document is excluded explicitly, and that exclusion is
      // load-bearing rather than tidy: a restored document leaves `wanted` the
      // instant OPEN_DOCUMENT_SUCCESS empties its suspended entry, so without
      // this it is "stale" on the very next tick — and `removeTdoLock` deletes
      // every one of this user's records for the TDO, including the one
      // `DocumentDetails` took milliseconds earlier. That unlocked the file
      // under active editing for up to a further heartbeat, which is precisely
      // the window a colleague needs to take it.
      // The project's exemption is conditioned on the SAME `lockTdo` that
      // decides whether it stays in `held` below. They disagreed, and the
      // disagreement abandoned live records: when a session lost the right
      // to hold the set, the project dropped out of `held` while the filter
      // still exempted it, so the record we had already written was never
      // refreshed and never released — not on the tick, not on close. Two
      // reviewers hitting the acquire race therefore left two live records
      // behind them for the full expiry, refusing every set-wide write in
      // the org after both had gone.
      const holdsProject = !!projectTdoId && mayTake;
      const stale = held.filter(
        (id) =>
          !wanted.includes(id) &&
          !(holdsProject && id === String(projectTdoId)) &&
          id !== active?.tdoId
      );
      if (stale.length) {
        yield* release(stale, userId);
      }

      // Refresh every wanted id, acquiring the new ones as a side effect —
      // the service is the same call for both, which is what keeps this loop
      // free of acquire/refresh branching.
      //
      // NOT `checkTdoLock`: that action's handlers write the single global
      // `tdoLockInfo` without reading the tdoId off the response, so firing it
      // for a background document overwrites the ON-SCREEN document's lock
      // verdict — in the worst direction, clearing a genuine lock and handing
      // two reviewers the same file. `serviceRefreshBackgroundTdoLock` writes
      // only our own record and asks nothing about ownership; assessing that
      // is the on-screen document's job, and `DocumentDetails` does it on the
      // way in, so a document switched back to is still judged correctly.
      const name = `${user?.kvp?.firstName ?? ''} ${
        user?.kvp?.lastName ?? ''
      }`.trim();
      for (const tdoId of wanted) {
        yield* put(serviceRefreshBackgroundTdoLock({ tdoId, userId, name }));
      }

      // THE SET IS ASKED ABOUT WHENEVER THE REQUEST HAS ONE, member open or
      // not — the same tick, the same service, one query it used to skip.
      //
      // Skipping it was how a reviewer parked outside the set got a refusal
      // with nobody to name: that branch never read the lock, so it could
      // say the set was unavailable but never who had it, and it said so
      // even when the set was free. An outsider is in exactly the position
      // the FOLDER VIEW is in — no lock, membership changes and deletes
      // allowed against the press-time gate alone — so a free set is no
      // block for it, and a held one names its holder like anywhere else.
      //
      // `stance` carries that difference down. It is not `mayTake` with a
      // third value bolted on: the question is what this session IS to the
      // set, and a free set means something different to each answer. See
      // `serviceRefreshProjectSetLock`.
      //
      // Membership arriving late reads as `outside` for the one pass
      // between OPEN_DOCUMENT_SUCCESS and LOAD_PROJECT_SUCCESS. That is the
      // honest answer there too — we hold nothing yet — and it now costs
      // the reviewer nothing, since an unheld set blocks an outsider no
      // more than it blocks the folder view.
      const askAboutTdoId = projectTdoId ?? requestProjectTdoId;
      if (askAboutTdoId) {
        yield* put(
          serviceRefreshProjectSetLock({
            projectTdoId: String(askAboutTdoId),
            userId,
            name,
            stance: !projectTdoId ? 'outside' : mayTake ? 'take' : 'readOnly',
          })
        );
      } else {
        // NOTHING TO REPORT IS ITSELF A REPORT. Without this the verdict was
        // written once and never withdrawn: switching to a request with no
        // merged document left the slice still saying locked, so an
        // unrelated file opened read-only behind a banner naming a merged
        // document it is not in.
        //
        // The page's unmount cleanup does not cover this: its deps are
        // `[dispatch]`, so it fires when the editor closes, not when the
        // open document changes. `externalRedactionStatus` avoids the same
        // trap by republishing per document.
        yield* put(setMergedSetBlock(null));
      }

      // `held` carries the project so closing releases it, but only when we
      // could have taken it. The release is a no-op otherwise —
      // `removeTdoLock` filters by `{ tdoId, userId }` and finds nothing of
      // ours — and listing an id we never wrote invites a later reader to
      // believe we hold it.
      held = holdsProject ? [...wanted, String(projectTdoId)] : wanted;
    }

    // Wake on the heartbeat, on the editor closing, or on the set of open
    // documents changing. CLOSE_DOCUMENT clears `suspendedSessions`, so
    // waiting a full minute to notice would leave a minute of locks on
    // documents nobody has open. OPEN_DOCUMENT_SUCCESS matters for the
    // opposite reason: it is the action that parks the outgoing document, and
    // `DocumentDetails` has already released that document's lock on the route
    // change — so a just-suspended dirty document sits UNLOCKED until the next
    // tick, up to a full LOCK_REFRESH, which is exactly the gap this saga
    // exists to close. (SWITCH_DOCUMENT_STARTED fires before the suspend, so
    // waking on it would only cost a pass that finds nothing new.)
    //
    // LOAD_PROJECT_SUCCESS (VE-31538) is the same argument again, for the
    // project lock: membership arrives asynchronously, AFTER
    // OPEN_DOCUMENT_SUCCESS, so the pass that runs on the open cannot yet
    // tell whether this document is a member. Without this wake the set sits
    // unlocked for up to a full LOCK_REFRESH on every cold open.
    const { closed } = yield* race({
      tick: delay(LOCK_REFRESH),
      // LOGOUT as well as CLOSE_DOCUMENT: on a shared workstation the next
      // user must not inherit locks this one is still holding, and logging out
      // does not route through the editor's close.
      closed: take([CLOSE_DOCUMENT, LOGOUT]),
      switched: take(wake),
    });

    if (closed && held.length) {
      // `userId` from the top of THIS pass, not a fresh select. On LOGOUT the
      // user slice may already be cleared by the time we resume, and re-reading
      // it then skipped the release while `held = []` threw the bookkeeping
      // away anyway — so the locks survived to the server-side expiry, which is
      // exactly the shared-workstation case this branch exists for.
      if (userId) {
        yield* release(held, userId);
        held = [];
      }
    }
  }
}
